import React from 'react';
import { useNavigate } from 'react-router-dom';

const h2Style = {
  color: '#fff',
  fontSize: 17,
  fontWeight: 600,
  marginTop: 36,
  marginBottom: 10,
  fontFamily: "var(--font-display, 'Fraunces', serif)",
  letterSpacing: '-0.2px',
};

const h3Style = {
  color: '#e2e8f0',
  fontSize: 14,
  fontWeight: 600,
  marginTop: 20,
  marginBottom: 6,
  fontFamily: "var(--font-body, 'Figtree', sans-serif)",
};

const pStyle = {
  color: '#b0b8c8',
  fontSize: 14,
  lineHeight: 1.75,
  margin: '0 0 12px',
  fontFamily: "var(--font-body, 'Figtree', sans-serif)",
};

const liStyle = {
  color: '#b0b8c8',
  fontSize: 14,
  lineHeight: 1.75,
  marginBottom: 6,
  fontFamily: "var(--font-body, 'Figtree', sans-serif)",
};

const strongStyle = { color: '#e2e8f0', fontWeight: 600 };

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div style={{
      background: '#0b1a29',
      minHeight: 'calc(100vh - 58px)',
      padding: 'clamp(32px, 5vw, 60px) 16px',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* Back link */}
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: '#e9a020', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
            marginBottom: 32, fontFamily: "var(--font-body, 'Figtree', sans-serif)",
          }}
        >
          ← Back to Dashboard
        </button>

        {/* Title */}
        <h1 style={{
          color: '#fff', fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700,
          marginBottom: 6, fontFamily: "var(--font-display, 'Fraunces', serif)",
          letterSpacing: '-0.5px',
        }}>
          Privacy Policy
        </h1>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 40, fontFamily: "var(--font-body, 'Figtree', sans-serif)" }}>
          Last updated: 01 January 2026
        </p>

        <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: 36 }} />

        {/* 1 */}
        <h2 style={h2Style}>1. Who We Are</h2>
        <p style={pStyle}>
          FathomVoyage ("we", "us", "our") operates the voyage cost intelligence platform at fathomvoyage.com.
          We are a data controller for the purposes of the UK General Data Protection Regulation (UK GDPR),
          the EU General Data Protection Regulation (EU GDPR 2016/679), and applicable data protection legislation.
        </p>
        <p style={pStyle}>
          For any questions regarding this policy or your personal data, contact us at:{' '}
          <a href="mailto:privacy@fathomvoyage.com" style={{ color: '#e9a020', textDecoration: 'none' }}>
            privacy@fathomvoyage.com
          </a>
        </p>

        {/* 2 */}
        <h2 style={h2Style}>2. Information We Collect</h2>

        <h3 style={h3Style}>2.1 Information you provide through LinkedIn Sign-In</h3>
        <p style={pStyle}>
          When you sign in using LinkedIn, we receive and process the following data from your LinkedIn profile,
          as authorised by you:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          {['Full name', 'Profile photograph', 'Primary email address', 'LinkedIn profile URL', 'Professional headline and company name'].map(item => (
            <li key={item} style={liStyle}>{item}</li>
          ))}
        </ul>
        <p style={pStyle}>
          We do not access your LinkedIn connections, messaging history, or any content you have posted on LinkedIn.
        </p>

        <h3 style={h3Style}>2.2 Information collected automatically</h3>
        <p style={pStyle}>When you use our platform, we automatically collect:</p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={liStyle}><strong style={strongStyle}>Usage data:</strong> pages visited, features used, interactions with dashboard modules, timestamps of activity</li>
          <li style={liStyle}><strong style={strongStyle}>Device and browser data:</strong> browser type and version, operating system, screen resolution, device type</li>
          <li style={liStyle}><strong style={strongStyle}>Network data:</strong> IP address, approximate geographic location derived from IP address</li>
          <li style={liStyle}><strong style={strongStyle}>Analytics identifiers:</strong> anonymised user identifiers generated by our analytics provider (PostHog)</li>
        </ul>
        <p style={pStyle}>
          We use privacy-first analytics. No analytics cookies are set until you explicitly accept via the consent
          banner shown on your first visit. You can withdraw consent at any time by clearing your browser's site
          data or by contacting{' '}
          <a href="mailto:privacy@fathomvoyage.com" style={{ color: '#e9a020', textDecoration: 'none' }}>
            privacy@fathomvoyage.com
          </a>.
          Our analytics are powered by PostHog, which operates without third-party advertising cookies.
        </p>

        <h3 style={h3Style}>2.3 Information we derive</h3>
        <p style={pStyle}>
          Based on your LinkedIn profile data, we may derive whether your professional profile aligns with the
          maritime industry. This classification is used solely for internal product development and to tailor
          our service to maritime professionals.
        </p>

        {/* 3 */}
        <h2 style={h2Style}>3. Legal Basis for Processing</h2>
        <p style={pStyle}>We process your personal data on the following legal bases under Article 6 of the GDPR:</p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={liStyle}><strong style={strongStyle}>Contractual necessity</strong> (Art. 6(1)(b)): Processing your LinkedIn profile data and usage data is necessary to provide you with access to the FathomVoyage platform and its features.</li>
          <li style={liStyle}><strong style={strongStyle}>Legitimate interests</strong> (Art. 6(1)(f)): We process analytics and derived professional classification data for the purposes of improving our product and understanding our user base. We have conducted a balancing test and determined that these interests do not override your fundamental rights and freedoms, given the professional (non-sensitive) nature of the data and the minimal privacy impact.</li>
          <li style={liStyle}><strong style={strongStyle}>Consent</strong> (Art. 6(1)(a)): Where required, we obtain your explicit consent before processing. You may withdraw consent at any time without affecting the lawfulness of processing carried out prior to withdrawal.</li>
        </ul>

        {/* 4 */}
        <h2 style={h2Style}>4. How We Use Your Information</h2>
        <p style={pStyle}>We use the information we collect to:</p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          {[
            'Authenticate your identity and provide access to the platform',
            'Deliver personalised voyage cost intelligence, bunker price monitoring, war risk assessments, and route comparison tools',
            'Generate AI-powered maritime insights relevant to your operational context',
            'Analyse platform usage to improve features and user experience',
            'Communicate with you regarding service updates, where permitted',
            'Ensure the security and integrity of our platform',
          ].map(item => <li key={item} style={liStyle}>{item}</li>)}
        </ul>
        <p style={pStyle}>We do not sell, rent, or share your personal data with third parties for their marketing purposes.</p>

        {/* 5 */}
        <h2 style={h2Style}>5. Data Sharing and Third-Party Processors</h2>
        <p style={pStyle}>
          We share your data only with the following categories of processors, each bound by data processing agreements:
        </p>

        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontSize: 12, fontFamily: "var(--font-body, 'Figtree', sans-serif)",
          }}>
            <thead>
              <tr>
                {['Processor', 'Purpose', 'Data Shared', 'Location'].map(h => (
                  <th key={h} style={{
                    padding: '10px 12px', textAlign: 'left',
                    color: '#e2e8f0', fontWeight: 600,
                    background: 'rgba(255,255,255,0.05)',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['Clerk (clerk.com)', 'Authentication', 'Name, email, LinkedIn profile', 'United States'],
                ['PostHog (posthog.com)', 'Product analytics', 'Anonymised usage data, user identifiers', 'European Union'],
                // ['Cloudflare (cloudflare.com)', 'Hosting, CDN, edge computing', 'IP address, request data', 'Global (edge locations)'],
                // ['Anthropic (anthropic.com)', 'AI-powered maritime insights', 'Aggregated, non-personal query data only', 'United States'],
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  {row.map((cell, j) => (
                    <td key={j} style={{
                      padding: '9px 12px', color: '#b0b8c8',
                      verticalAlign: 'top',
                    }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={pStyle}>
          Our platform is served via enterprise-grade edge infrastructure with encryption in transit and at rest. For transfers of personal data outside the European Economic Area (EEA) or the United Kingdom, we rely
          on Standard Contractual Clauses (SCCs) approved by the European Commission, or the processor's
          participation in an adequate transfer mechanism.
        </p>

        {/* 6 */}
        <h2 style={h2Style}>6. Data Retention</h2>
        <p style={pStyle}>We retain your personal data for as long as your account remains active. Specifically:</p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={liStyle}><strong style={strongStyle}>Account data</strong> (name, email, LinkedIn profile): retained until you delete your account or request erasure</li>
          <li style={liStyle}><strong style={strongStyle}>Usage analytics:</strong> retained in identifiable form for 24 months, after which data is anonymised and retained for statistical purposes only</li>
          <li style={liStyle}><strong style={strongStyle}>AI query logs:</strong> retained in anonymised form for 12 months for service improvement, with no link to your personal identity</li>
        </ul>
        <p style={pStyle}>
          Upon account deletion, your personal data is purged from our active systems within 30 days. Residual
          copies in encrypted backups are overwritten within 90 days.
        </p>

        {/* 7 */}
        <h2 style={h2Style}>7. Your Rights</h2>
        <p style={pStyle}>Under the GDPR, you have the following rights:</p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={liStyle}><strong style={strongStyle}>Right of access</strong> (Art. 15): Request a copy of all personal data we hold about you.</li>
          <li style={liStyle}><strong style={strongStyle}>Right to rectification</strong> (Art. 16): Request correction of inaccurate or incomplete personal data.</li>
          <li style={liStyle}><strong style={strongStyle}>Right to erasure</strong> (Art. 17): Request deletion of your personal data ("right to be forgotten").</li>
          <li style={liStyle}><strong style={strongStyle}>Right to restriction</strong> (Art. 18): Request that we limit how we process your data.</li>
          <li style={liStyle}><strong style={strongStyle}>Right to data portability</strong> (Art. 20): Receive your personal data in a structured, commonly used, machine-readable format.</li>
          <li style={liStyle}><strong style={strongStyle}>Right to object</strong> (Art. 21): Object to processing based on legitimate interests, including any profiling.</li>
          <li style={liStyle}><strong style={strongStyle}>Right to withdraw consent:</strong> Where processing is based on consent, withdraw it at any time.</li>
        </ul>
        <p style={pStyle}>
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:privacy@fathomvoyage.com" style={{ color: '#e9a020', textDecoration: 'none' }}>
            privacy@fathomvoyage.com
          </a>
          . We will respond within 30 days of receiving your request. If we require an extension, we will inform
          you within the initial 30-day period.
        </p>
        <p style={pStyle}>
          You also have the right to lodge a complaint with a supervisory authority. In the UK, this is the
          Information Commissioner's Office (ICO) at ico.org.uk. In the EU, contact your local data protection
          authority.
        </p>

        {/* 8 */}
        <h2 style={h2Style}>8. Data Security</h2>
        <p style={pStyle}>
          We implement appropriate technical and organisational measures to protect your personal data against
          unauthorised access, alteration, disclosure, or destruction. These measures include:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          {[
            'Encryption of data in transit using TLS 1.3',
            'Encryption of data at rest',
            'Access controls limiting data access to authorised personnel only',
            'Regular security assessments of our infrastructure and third-party processors',
            "Use of Cloudflare's edge security, DDoS protection, and web application firewall",
          ].map(item => <li key={item} style={liStyle}>{item}</li>)}
        </ul>
        <p style={pStyle}>
          No method of transmission or storage is completely secure. While we strive to protect your personal
          data, we cannot guarantee its absolute security.
        </p>

        {/* 9 */}
        <h2 style={h2Style}>9. Children's Privacy</h2>
        <p style={pStyle}>
          FathomVoyage is a professional maritime industry tool and is not directed at individuals under the age
          of 18. We do not knowingly collect personal data from children. If we become aware that a child has
          provided us with personal data, we will take steps to delete such data promptly.
        </p>

        {/* 10 */}
        <h2 style={h2Style}>10. Changes to This Policy</h2>
        <p style={pStyle}>
          We may update this privacy policy from time to time. We will notify you of material changes by posting
          the updated policy on this page and updating the "Last updated" date. For significant changes, we will
          provide prominent notice on the platform. Your continued use of FathomVoyage after changes are posted
          constitutes acceptance of the revised policy.
        </p>

        {/* 11 */}
        <h2 style={h2Style}>11. Contact</h2>
        <p style={pStyle}>For all privacy-related enquiries, data subject requests, or complaints:</p>
        <p style={pStyle}>
          <strong style={strongStyle}>Email:</strong>{' '}
          <a href="mailto:privacy@fathomvoyage.com" style={{ color: '#e9a020', textDecoration: 'none' }}>
            privacy@fathomvoyage.com
          </a>
        </p>

        <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)', margin: '36px 0 24px' }} />
        <p style={{ color: '#475569', fontSize: 12, fontFamily: "var(--font-body, 'Figtree', sans-serif)", fontStyle: 'italic' }}>
          This policy is governed by the laws of England and Wales.
        </p>

      </div>
    </div>
  );
}
